/**
 * Busca de entidade por digitação (server-side) **com cadastro rápido**. Reusável
 * para pessoas e empresas: consulta `{endpoint}?busca=` a cada tecla e, se a pessoa
 * ou empresa ainda não existe, oferece criá-la ali mesmo com os campos mínimos — o
 * vínculo já nasce pronto e fica disponível quando aquele cadastro for aberto depois.
 *
 * Emite o item escolhido (com `id`) por `onSelecionar`. É a peça das correlações
 * pessoa↔pessoa e pessoa↔empresa.
 */
import { Plus, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CampoDocumento } from '@/components/CampoDocumento';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { listarRecurso, criarRecurso } from '@/lib/cadastroApi';
import type { Formato } from '@/lib/mascaras';
import { toast } from 'sonner';

export type ItemBusca = { id: string } & Record<string, unknown>;

export type CampoRapido = {
  chave: string;
  rotulo: string;
  tipo: 'texto' | 'documento';
  formato?: Formato;
  obrigatorio?: boolean;
};

export type ConfigBusca = {
  endpoint: string;
  rotulo: (item: ItemBusca) => string;
  sub?: (item: ItemBusca) => string | null;
  camposRapido: CampoRapido[]; // o 1º recebe o texto digitado como valor inicial
  placeholder?: string;
};

export function ComboBuscaEntidade({
  config,
  onSelecionar,
  excluirId,
}: {
  config: ConfigBusca;
  onSelecionar: (item: ItemBusca | null) => void;
  excluirId?: string;
}) {
  const [filtro, setFiltro] = useState('');
  const [resultados, setResultados] = useState<ItemBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [escolhido, setEscolhido] = useState<ItemBusca | null>(null);
  const [aberto, setAberto] = useState(false);
  const [criando, setCriando] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!aberto) return;
    function fora(e: MouseEvent) {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  function digitar(termo: string) {
    setFiltro(termo);
    setAberto(true);
    setCriando(false);
    clearTimeout(debounce.current);
    if (!termo.trim()) {
      setResultados([]);
      return;
    }
    setBuscando(true);
    debounce.current = setTimeout(async () => {
      const r = await listarRecurso<ItemBusca>(config.endpoint, { busca: termo, limite: 20 });
      setBuscando(false);
      if (r.ok) setResultados(r.dados.itens.filter((i) => i.id !== excluirId));
    }, 300);
  }

  function escolher(item: ItemBusca) {
    setEscolhido(item);
    onSelecionar(item);
    setAberto(false);
    setCriando(false);
    setFiltro('');
  }

  function limpar() {
    setEscolhido(null);
    onSelecionar(null);
    setFiltro('');
    setResultados([]);
  }

  if (escolhido) {
    const sub = config.sub?.(escolhido);
    return (
      <div className="flex h-10 items-center justify-between gap-2 rounded-md border border-border bg-input px-3">
        <span className="truncate text-[15px] text-text">
          {config.rotulo(escolhido)}
          {sub && <span className="ml-2 tipo-dado text-text-tertiary">{sub}</span>}
        </span>
        <button
          type="button"
          aria-label="Trocar"
          onClick={limpar}
          className="rounded-sm p-1 text-text-tertiary hover:text-text"
        >
          <X className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={raiz} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-tertiary" />
        <Input
          value={filtro}
          onChange={(e) => digitar(e.target.value)}
          onFocus={() => setAberto(true)}
          placeholder={config.placeholder ?? 'Buscar…'}
          className="pl-9"
        />
      </div>

      {aberto && filtro.trim() && (
        <div className="absolute z-[var(--z-dropdown)] mt-1 w-full rounded-lg border border-border bg-surface p-1 shadow-[var(--shadow-md)]">
          {criando ? (
            <FormularioRapido
              config={config}
              textoInicial={filtro}
              onCriado={escolher}
              onCancelar={() => setCriando(false)}
            />
          ) : (
            <ul className="max-h-64 overflow-y-auto">
              {buscando ? (
                <li className="flex justify-center px-3 py-3">
                  <Spinner />
                </li>
              ) : (
                <>
                  {resultados.map((item) => {
                    const sub = config.sub?.(item);
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => escolher(item)}
                          className="flex w-full flex-col items-start rounded-sm px-3 py-2 text-left outline-none hover:bg-surface-hover focus-visible:bg-surface-hover"
                        >
                          <span className="text-[15px] text-text">{config.rotulo(item)}</span>
                          {sub && <span className="tipo-dado text-text-tertiary">{sub}</span>}
                        </button>
                      </li>
                    );
                  })}
                  {!buscando && resultados.length === 0 && (
                    <li className="px-3 py-2 text-[13px] text-text-tertiary">Nada encontrado.</li>
                  )}
                  <li className="border-t border-border">
                    <button
                      type="button"
                      onClick={() => setCriando(true)}
                      className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-[15px] text-accent outline-none hover:bg-surface-hover"
                    >
                      <Plus className="size-4" />
                      Cadastrar rápido “{filtro.trim()}”
                    </button>
                  </li>
                </>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Mini-formulário de cadastro rápido: os campos mínimos, o 1º já com o texto digitado. */
function FormularioRapido({
  config,
  textoInicial,
  onCriado,
  onCancelar,
}: {
  config: ConfigBusca;
  textoInicial: string;
  onCriado: (item: ItemBusca) => void;
  onCancelar: () => void;
}) {
  const [valores, setValores] = useState<Record<string, string>>(() => ({
    [config.camposRapido[0].chave]: textoInicial.trim(),
  }));
  const [salvando, setSalvando] = useState(false);
  const falta = config.camposRapido.some((c) => c.obrigatorio && !valores[c.chave]?.trim());

  // Sem elemento <form>: este mini-cadastro vive dentro de outros formulários (drawers, painéis),
  // e um <form> aninhado é HTML inválido — o navegador o descarta e o submit vaza para o form de
  // fora. Botão comum + Enter nos campos dão o mesmo gesto sem o aninhamento.
  async function criar() {
    if (falta || salvando) return;
    setSalvando(true);
    const corpo: Record<string, string> = {};
    for (const c of config.camposRapido) if (valores[c.chave]?.trim()) corpo[c.chave] = valores[c.chave].trim();
    const r = await criarRecurso<ItemBusca>(config.endpoint, corpo);
    setSalvando(false);
    if (!r.ok) return void toast.error(r.erro);
    onCriado(r.dados);
  }

  function aoTeclar(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void criar();
    }
  }

  return (
    <div className="flex flex-col gap-3 p-2" onKeyDown={aoTeclar}>
      {config.camposRapido.map((c) =>
        c.tipo === 'documento' ? (
          <CampoDocumento
            key={c.chave}
            rotulo={c.rotulo}
            valor={valores[c.chave] ?? ''}
            onChange={(v) => setValores((a) => ({ ...a, [c.chave]: v }))}
            formato={c.formato ?? 'cpfcnpj'}
          />
        ) : (
          <label key={c.chave} className="flex flex-col gap-2">
            <span className="tipo-rotulo">{c.rotulo}</span>
            <Input
              autoFocus={c === config.camposRapido[0]}
              value={valores[c.chave] ?? ''}
              onChange={(e) => setValores((a) => ({ ...a, [c.chave]: e.target.value }))}
            />
          </label>
        ),
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="fantasma" size="sm" onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="button" variant="secundario" size="sm" onClick={() => void criar()} disabled={falta || salvando}>
          {salvando ? <Spinner /> : 'Criar e vincular'}
        </Button>
      </div>
    </div>
  );
}
